import {
  STROKE_COLOR_ACTIVE,
  STROKE_COLOR_GREEN,
  STROKE_COLOR_PASSIVE,
} from '@/web-components/import-wizard-v2/constants';
import {
  Category,
  ProductPrintPosition,
  RoundItemConfiguration,
  mm2px,
} from '@midocean/webapps-shared-domain';
import { getActivePanelNumber } from './drawPanelLines';
import { PanelTargetLine } from '@/typings/ProductPrintData';
import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import { useStore as useProductStore } from '@/stores/v2/product';

export function generatePanelSections(activePosition: ProductPrintPosition) {
  const elements = [];
  if (activePosition.category === Category.RoundItem) {
    const originalImageWidth =
      (activePosition.originalImageWidthPx *
        activePosition.originalImageWebDpi) /
      activePosition.originalImageDpi;
    const categoryConfig =
      activePosition.categoryConfiguration as RoundItemConfiguration;
    const panelWidth = mm2px(
      categoryConfig.productDiameter,
      activePosition.originalImageWebDpi || 96
    );
    const centeredPanelXCoordinate = (originalImageWidth - panelWidth) / 2;

    for (let index = 0; index < categoryConfig.numberOfPanels - 1; index++) {
      elements.push(
        centeredPanelXCoordinate +
          (panelWidth * (index + 1)) / categoryConfig.numberOfPanels
      );
    }

    return elements;
  } else {
    return [];
  }
}

export function drawPanelSections(
  activePosition: ProductPrintPosition,
  y: number,
  height: number
) {
  if (activePosition.category === Category.RoundItem) {
    const originalImageWidth =
      (activePosition.originalImageWidthPx *
        activePosition.originalImageWebDpi) /
      activePosition.originalImageDpi;
    const categoryConfig =
      activePosition.categoryConfiguration as RoundItemConfiguration;
    const panelWidth = mm2px(
      categoryConfig.productDiameter,
      activePosition.originalImageWebDpi || 96
    );
    const centeredPanelXCoordinate = (originalImageWidth - panelWidth) / 2;

    const elements = [];

    for (let index = 0; index < categoryConfig.numberOfPanels - 1; index++) {
      elements.push({
        strokeColor: STROKE_COLOR_GREEN,
        x1:
          centeredPanelXCoordinate +
          (panelWidth * (index + 1)) / categoryConfig.numberOfPanels,
        x2:
          centeredPanelXCoordinate +
          (panelWidth * (index + 1)) / categoryConfig.numberOfPanels,
        y1: y + height / 8,
        y2: y + height - height / 8,
      });
    }

    return elements;
  }
}

export function updateTargetLines(
  shape: AnnotationShape,
  centerCoordinatesOfPanels: PanelTargetLine[],
  panelsPositions: number[],
  isRemovingShape?: boolean
) {
  const productStore = useProductStore();
  const activePanelNumber = getActivePanelNumber(shape.x, panelsPositions);
  const centerOfPanelVerticalLine = centerCoordinatesOfPanels.find((item) => {
    if (
      Math.abs(
        Math.round(item.x1 + (item.x2 - item.x1) / 2) -
          Math.round(shape.x + shape.width / 2)
      ) < 10 &&
      activePanelNumber === item.panelNo &&
      item.isInteractive
    ) {
      return item;
    }
  });

  const centerOfPanelHorizontalLine = centerCoordinatesOfPanels.find((item) => {
    if (
      Math.abs(
        Math.round(item.y1 + (item.y2 - item.y1) / 2) -
          Math.round(shape.y + shape.height / 2)
      ) < 10 &&
      activePanelNumber === item.panelNo &&
      item.isInteractive &&
      item.id !== centerOfPanelVerticalLine?.id
    ) {
      return item;
    }
  });

  if (
    centerOfPanelVerticalLine &&
    centerOfPanelVerticalLine.isInteractive &&
    centerOfPanelHorizontalLine &&
    centerOfPanelHorizontalLine.isInteractive &&
    !isRemovingShape
  ) {
    const objToUpdateIndexX = centerCoordinatesOfPanels.findIndex(
      (item) => item.id === centerOfPanelVerticalLine.id
    );
    const objToUpdateIndexY = centerCoordinatesOfPanels.findIndex(
      (item) => item.id === centerOfPanelHorizontalLine.id
    );

    centerOfPanelVerticalLine.strokeColor = STROKE_COLOR_ACTIVE;
    centerOfPanelHorizontalLine.strokeColor = STROKE_COLOR_ACTIVE;

    centerCoordinatesOfPanels[objToUpdateIndexX] = {
      ...centerCoordinatesOfPanels[objToUpdateIndexX],
      strokeColor: STROKE_COLOR_ACTIVE,
    };
    centerCoordinatesOfPanels[objToUpdateIndexY] = {
      ...centerCoordinatesOfPanels[objToUpdateIndexY],
      strokeColor: STROKE_COLOR_ACTIVE,
    };

    productStore.setCenterCoordinatesOfPanels([...centerCoordinatesOfPanels]);
  } else {
    productStore.setCenterCoordinatesOfPanels([
      ...centerCoordinatesOfPanels.map((obj) => {
        obj.strokeColor = STROKE_COLOR_PASSIVE;
        return obj;
      }),
    ]);
  }

  return centerCoordinatesOfPanels;
}
